import { Language } from '../types';

export const talkToDataTranslations: Record<Language, Record<string, string>> = {
  en: {
    // Talk to Your Data Page
    interactNatural: "Talk to Your Data",
    interactDesc: "Interact with your data using natural language and get instant insights without navigating complex reports.",
    getStarted: "Get Started",
    
    // Optimization Section in Talk to Your Data
    optimizeTitle: "Optimize Your Data Analysis Process",
    optimizeDesc: "Our solution streamlines access to insights, eliminates the need for technical queries, and makes your entire organization more data-driven.",
    
    // Features
    instantAnswers: "Instant Answers",
    instantDesc: "Get immediate responses to business questions without building reports or waiting for analysis.",
    
    naturalLanguage: "Natural Language",
    naturalDesc: "Ask questions in everyday language - no need to learn technical query languages.",
    
    accessAnywhere: "Access Anywhere",
    accessDesc: "Use your favorite chat platform to interact with your data from anywhere, anytime.",
    
    deepIntegration: "Deep Integration",
    deepDesc: "Connect directly to your data sources for accurate, real-time answers based on your actual data.",
    
    // How It Works
    howWorks: "How It Works",
    simplePowerful: "Simple, Powerful, Accessible",
    solutionDesc: "Our solution connects your data sources to familiar chat interfaces, allowing you to ask questions and get visualized answers instantly.",
    
    connectData: "1. Connect Your Data",
    connectDesc: "We integrate with your existing data warehouse, databases, or BI tools.",
    
    chooseInterface: "2. Choose Your Interface",
    interfaceDesc: "Decide which chat platform you want to use - we support all major business messaging apps.",
    
    startAsking: "3. Start Asking Questions",
    askingDesc: "Simply type your questions in natural language and receive insights with visualizations.",
    
    // Supported Platforms
    supportedPlatforms: "Supported Platforms",
    integrationDesc: "Our solution integrates seamlessly with the communication tools your team already uses every day.",
    
    // Benefits Section
    keyBenefits: "Key Benefits",
    businessImpact: "Real Business Impact",
    
    // Benefit Items
    democratizeData: "Democratize Data Access",
    democratizeDesc: "Give everyone in your organization the power to access insights, regardless of technical skill.",
    
    saveTechnicalTime: "Save Technical Resources",
    technicalDesc: "Free your data team from routine reporting tasks so they can focus on higher-value analysis.",
    
    fasterDecisions: "Faster Decision Making",
    decisionsDesc: "Reduce the time from question to insight dramatically, accelerating your decision cycles.",
    
    improveAdoption: "Improve Data Adoption",
    adoptionDesc: "When data is easier to access, more people will use it to inform their decisions.",
    
    reduceTraining: "Reduce Training Costs",
    trainingDesc: "Minimal training required - if you can chat, you can analyze data.",
    
    scalableArchitectureSection: "Scalable Architecture",
    architectureDescText: "Whether you have 10 users or 10,000, our solution scales with your needs.",
    
    // Contact Section
    startConversation: "Start the Conversation with Your Data",
    readyExplore: "Ready to explore how talking to your data can transform your business?",
    scheduleDemo: "Schedule a Demo",
  },
  de: {
    // Talk to Your Data Page
    interactNatural: "Sprechen Sie mit Ihren Daten",
    interactDesc: "Interagieren Sie mit Ihren Daten mithilfe natürlicher Sprache und erhalten Sie sofortige Erkenntnisse, ohne komplexe Berichte zu navigieren.",
    getStarted: "Loslegen",
    
    // Optimization Section in Talk to Your Data
    optimizeTitle: "Optimieren Sie Ihren Datenanalyseprozess",
    optimizeDesc: "Unsere Lösung rationalisiert den Zugang zu Erkenntnissen, eliminiert den Bedarf an technischen Abfragen und macht Ihre gesamte Organisation datengesteuerter.",
    
    // Features
    instantAnswers: "Sofortige Antworten",
    instantDesc: "Erhalten Sie sofortige Antworten auf Geschäftsfragen, ohne Berichte zu erstellen oder auf Analysen zu warten.",
    
    naturalLanguage: "Natürliche Sprache",
    naturalDesc: "Stellen Sie Fragen in Alltagssprache - keine Notwendigkeit, technische Abfragesprachen zu erlernen.",
    
    accessAnywhere: "Überall Zugriff",
    accessDesc: "Nutzen Sie Ihre bevorzugte Chat-Plattform, um von überall und jederzeit mit Ihren Daten zu interagieren.",
    
    deepIntegration: "Tiefe Integration",
    deepDesc: "Verbinden Sie sich direkt mit Ihren Datenquellen für genaue Echtzeitantworten basierend auf Ihren tatsächlichen Daten.",
    
    // How It Works
    howWorks: "Wie es funktioniert",
    simplePowerful: "Einfach, Leistungsstark, Zugänglich",
    solutionDesc: "Unsere Lösung verbindet Ihre Datenquellen mit vertrauten Chat-Schnittstellen, sodass Sie Fragen stellen und sofort visualisierte Antworten erhalten können.",
    
    connectData: "1. Verbinden Sie Ihre Daten",
    connectDesc: "Wir integrieren mit Ihrem bestehenden Data Warehouse, Datenbanken oder BI-Tools.",
    
    chooseInterface: "2. Wählen Sie Ihre Schnittstelle",
    interfaceDesc: "Entscheiden Sie, welche Chat-Plattform Sie verwenden möchten - wir unterstützen alle wichtigen Business-Messaging-Apps.",
    
    startAsking: "3. Beginnen Sie, Fragen zu stellen",
    askingDesc: "Tippen Sie einfach Ihre Fragen in natürlicher Sprache ein und erhalten Sie Erkenntnisse mit Visualisierungen.",
    
    // Supported Platforms
    supportedPlatforms: "Unterstützte Plattformen",
    integrationDesc: "Unsere Lösung integriert sich nahtlos in die Kommunikationstools, die Ihr Team bereits täglich verwendet.",
    
    // Benefits Section
    keyBenefits: "Hauptvorteile",
    businessImpact: "Reale Geschäftsauswirkungen",
    
    // Benefit Items
    democratizeData: "Demokratisieren Sie den Datenzugang",
    democratizeDesc: "Geben Sie jedem in Ihrer Organisation die Macht, auf Erkenntnisse zuzugreifen, unabhängig von technischen Fähigkeiten.",
    
    saveTechnicalTime: "Sparen Sie technische Ressourcen",
    technicalDesc: "Befreien Sie Ihr Datenteam von Routineberichtsaufgaben, damit sie sich auf höherwertige Analysen konzentrieren können.",
    
    fasterDecisions: "Schnellere Entscheidungsfindung",
    decisionsDesc: "Reduzieren Sie die Zeit von der Frage zur Erkenntnis drastisch und beschleunigen Sie Ihre Entscheidungszyklen.",
    
    improveAdoption: "Verbessern Sie die Datenannahme",
    adoptionDesc: "Wenn Daten leichter zugänglich sind, werden mehr Menschen sie nutzen, um ihre Entscheidungen zu informieren.",
    
    reduceTraining: "Reduzieren Sie Schulungskosten",
    trainingDesc: "Minimaler Schulungsbedarf - wenn Sie chatten können, können Sie Daten analysieren.",
    
    scalableArchitectureSection: "Skalierbare Architektur",
    architectureDescText: "Ob Sie 10 Benutzer oder 10.000 haben, unsere Lösung skaliert mit Ihren Bedürfnissen.",
    
    // Contact Section
    startConversation: "Beginnen Sie das Gespräch mit Ihren Daten",
    readyExplore: "Bereit zu erkunden, wie das Sprechen mit Ihren Daten Ihr Geschäft transformieren kann?",
    scheduleDemo: "Demo planen",
  },
  sk: {
    // Talk to Your Data Page - using English as fallback for now
    interactNatural: "Hovorte s vašimi dátami",
    interactDesc: "Interact with your data using natural language and get instant insights without navigating complex reports.",
    getStarted: "Začať",
    // Using English fallbacks for all other keys
    optimizeTitle: "Optimize Your Data Analysis Process",
    optimizeDesc: "Our solution streamlines access to insights, eliminates the need for technical queries, and makes your entire organization more data-driven.",
    scheduleDemo: "Schedule a Demo",
  },
  cs: {
    // Talk to Your Data Page
    interactNatural: "Mluvte se svými daty",
    interactDesc: "Interagujte se svými daty pomocí přirozeného jazyka a získejte okamžité poznatky bez navigace složitými reporty.",
    getStarted: "Začít",
    
    // Optimization Section in Talk to Your Data
    optimizeTitle: "Optimalizujte svůj proces analýzy dat",
    optimizeDesc: "Naše řešení zjednodušuje přístup k poznatkům, eliminuje potřebu technických dotazů a činí celou vaši organizaci datově orientovanější.",
    
    // Features
    instantAnswers: "Okamžité odpovědi",
    instantDesc: "Získejte okamžité odpovědi na obchodní otázky bez vytváření reportů nebo čekání na analýzu.",
    
    naturalLanguage: "Přirozený jazyk",
    naturalDesc: "Ptejte se v běžném jazyce - není třeba učit se technické dotazovací jazyky.",
    
    accessAnywhere: "Přístup odkudkoli",
    accessDesc: "Používejte svou oblíbenou chatovací platformu k interakci se svými daty odkudkoli a kdykoli.",
    
    deepIntegration: "Hluboká integrace",
    deepDesc: "Připojte se přímo ke svým zdrojům dat pro přesné, reálné odpovědi založené na vašich skutečných datech.",
    
    // How It Works
    howWorks: "Jak to funguje",
    simplePowerful: "Jednoduché, výkonné, dostupné",
    solutionDesc: "Naše řešení propojuje vaše zdroje dat se známými chatovacími rozhraními, což vám umožňuje klást otázky a okamžitě získávat vizualizované odpovědi.",
    
    connectData: "1. Připojte svá data",
    connectDesc: "Integrujeme se s vaším stávajícím datovým skladem, databázemi nebo BI nástroji.",
    
    chooseInterface: "2. Vyberte si rozhraní",
    interfaceDesc: "Rozhodněte se, kterou chatovací platformu chcete použít - podporujeme všechny hlavní obchodní komunikační aplikace.",
    
    startAsking: "3. Začněte klást otázky",
    askingDesc: "Jednoduše napište své otázky v přirozeném jazyce a přijímejte poznatky s vizualizacemi.",
    
    // Supported Platforms
    supportedPlatforms: "Podporované platformy",
    integrationDesc: "Naše řešení se bezproblémově integruje s komunikačními nástroji, které váš tým již každý den používá.",
    
    // Benefits Section
    keyBenefits: "Klíčové výhody",
    businessImpact: "Skutečný obchodní dopad",
    
    // Benefit Items
    democratizeData: "Demokratizujte přístup k datům",
    democratizeDesc: "Dejte každému ve vaší organizaci moc přistupovat k poznatkům, bez ohledu na technické dovednosti.",
    
    saveTechnicalTime: "Ušetřete technické zdroje",
    technicalDesc: "Osvoboďte svůj datový tým od rutinních reportovacích úkolů, aby se mohl zaměřit na vyšší hodnotu analýzy.",
    
    fasterDecisions: "Rychlejší rozhodování",
    decisionsDesc: "Dramaticky snižte čas od otázky k poznatku a zrychlete své rozhodovací cykly.",
    
    improveAdoption: "Zlepšete adopci dat",
    adoptionDesc: "Když jsou data snadněji dostupná, více lidí je bude používat k informování svých rozhodnutí.",
    
    reduceTraining: "Snižte náklady na školení",
    trainingDesc: "Minimální potřeba školení - pokud umíte chatovat, umíte analyzovat data.",
    
    scalableArchitectureSection: "Škálovatelná architektura",
    architectureDescText: "Ať máte 10 uživatelů nebo 10 000, naše řešení se škáluje podle vašich potřeb.",
    
    // Contact Section
    startConversation: "Začněte konverzaci se svými daty",
    readyExplore: "Připraveni prozkoumat, jak mluvení se svými daty může transformovat vaše podnikání?",
    scheduleDemo: "Naplánovat demo",
  }
};
